/**
 * Navbar Component
 * Main navigation with theme toggle
 */

import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FiMenu, FiX, FiMoon, FiSun } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { isAuthenticated, isAdmin, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/jobs', label: 'Experience' },
    { path: '/projects', label: 'Projects' },
    { path: '/blog', label: 'Blog' },
    { path: '/teaching', label: 'Teaching' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' },
    { path: 'https://tsi400.com', label: 'Consulting', external: true },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <Nav>
      <Container>
        <Logo to="/">
          <LogoImage src="/Rlogo.png" alt="Richard Plotka" />
          <LogoText>Richard Plotka</LogoText>
        </Logo>

        <MenuIcon onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </MenuIcon>

        <NavMenu $isOpen={isOpen}>
          {navLinks.map((link) =>
            link.external ? (
              <ExternalNavLink
                key={link.path}
                href={link.path}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsOpen(false)}
              >
                {link.label} &#8599;
              </ExternalNavLink>
            ) : (
              <NavLink
                key={link.path}
                to={link.path}
                $active={isActive(link.path)}
                onClick={() => setIsOpen(false)}
              >
                {link.label}
              </NavLink>
            )
          )}

          {isAuthenticated && isAdmin && (
            <NavLink
              to="/superadmin"
              $active={isActive('/superadmin')}
              onClick={() => setIsOpen(false)}
            >
              Admin
            </NavLink>
          )}

          {isAuthenticated && (
            <NavLink
              to="/settings"
              $active={isActive('/settings')}
              onClick={() => setIsOpen(false)}
            >
              Settings
            </NavLink>
          )}

          <ThemeToggle onClick={toggleTheme} title="Toggle theme">
            {isDark ? <FiSun size={20} /> : <FiMoon size={20} />}
          </ThemeToggle>

          {isAuthenticated ? (
            <Button onClick={logout}>Logout</Button>
          ) : (
            <Button as={Link} to="/login" onClick={() => setIsOpen(false)}>
              Login
            </Button>
          )}
        </NavMenu>
      </Container>
    </Nav>
  );
};

const Nav = styled.nav`
  position: sticky;
  top: 0;
  z-index: 1000;
  background: var(--bg-primary);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--border-color);
  padding: 1rem 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled(Link)`
  font-size: 1.5rem;
  font-weight: 700;
  font-family: 'Space Grotesk', sans-serif;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const LogoImage = styled.img`
  height: 40px;
  width: auto;
  object-fit: contain;
`;

const LogoText = styled.span`
  background: var(--accent-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const MenuIcon = styled.div`
  display: none;
  cursor: pointer;
  color: var(--text-primary);

  @media (max-width: 768px) {
    display: block;
  }
`;

const NavMenu = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    position: fixed;
    top: 70px;
    right: ${({ $isOpen }) => ($isOpen ? '0' : '-100%')};
    width: 100%;
    height: calc(100vh - 70px);
    background: var(--bg-secondary);
    flex-direction: column;
    justify-content: flex-start;
    padding: 2rem;
    gap: 1.5rem;
    transition: right 0.3s ease;
  }
`;

const NavLink = styled(Link)`
  color: var(--text-primary);
  font-weight: 500;
  position: relative;
  transition: color 0.3s ease;

  ${({ $active }) =>
    $active &&
    `
    color: var(--accent-primary);
    
    &::after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 0;
      width: 100%;
      height: 2px;
      background: var(--accent-gradient);
    }
  `}

  &:hover {
    color: var(--accent-primary);
  }

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const ExternalNavLink = styled.a`
  color: var(--text-primary);
  font-weight: 500;
  position: relative;
  transition: color 0.3s ease;
  text-decoration: none;

  &:hover {
    color: var(--accent-primary);
  }

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const ThemeToggle = styled.button`
  background: transparent;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 0.5rem;
  transition: all 0.3s ease;

  &:hover {
    background: var(--bg-tertiary);
    transform: scale(1.1);
  }
`;

const Button = styled.button`
  padding: 0.625rem 1.5rem;
  background: var(--accent-gradient);
  color: white;
  border-radius: 0.5rem;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }
`;

// Memoize Navbar to prevent unnecessary re-renders
export default React.memo(Navbar);
